import { useCallback } from 'react';
import useHeic2any from '@/ueshook/useHeic2any';
import tryCatchPromise from '@/utils/tryCatchPromise';
import { toast } from 'sonner';
import type { BaseImageStatus } from '@/components/batchToolsComp/layoutContext/ImageProcessingContext';
import { feBot } from '@/monitor';

/**
 * 图像预处理Hook - 纯粹的图像处理逻辑
 * @description 提供HEIC转换、重复检测、图像验证等功能
 */
export function useImageProcessor(
  from: string = 'useImageProcessor_default_from'
) {
  const { convertHeicToAny } = useHeic2any();

  /**
   * 使用 createImageBitmap 检测图像完整性（现代方案）
   * @param file 图像文件
   */
  const validateImageIntegrityModern = useCallback(
    async (file: File): Promise<boolean> => {
      try {
        // createImageBitmap 性能更好，支持更多格式
        const imageBitmap = await createImageBitmap(file);
        imageBitmap.close(); // 释放资源
        return true;
      } catch (error) {
        return false;
      }
    },
    []
  );

  /**
   * 使用 Image 对象检测图像完整性（兼容性方案） - 针对老旧版本浏览器或IE
   * @param file 图像文件
   */
  const validateImageIntegrityLegacy = useCallback(
    (file: File): Promise<boolean> => {
      return new Promise((resolve) => {
        let localUrl = '';
        try {
          localUrl = URL.createObjectURL(file);
        } catch (error) {
          resolve(false);
          return;
        }

        const img = new Image();
        const cleanup = () => {
          try {
            URL.revokeObjectURL(localUrl);
          } catch (error) {
            console.warn(`Failed to revoke URL during validation:`, error);
          }
        };

        img.onload = () => {
          cleanup();
          resolve(true);
        };
        img.onerror = () => {
          cleanup();
          resolve(false);
        };
        img.src = localUrl;
      });
    },
    []
  );

  /**
   * 智能图像完整性校验（优化方案）
   * @param file 图像文件
   */
  const validateImageIntegrity = useCallback(
    async (file: File): Promise<boolean> => {
      // 1. 根据浏览器支持选择检测方案
      if (typeof createImageBitmap === 'function') {
        // 现代浏览器使用 createImageBitmap（性能更好）
        return await validateImageIntegrityModern(file);
      } else {
        // 旧浏览器降级到 Image 方案
        return await validateImageIntegrityLegacy(file);
      }
    },
    [validateImageIntegrityModern, validateImageIntegrityLegacy]
  );

  /**
   * 检查并处理图像文件
   * @param fileList 待处理的文件列表
   * @param existingImages 已存在的图像（用于重复检测）
   */
  const processImageFiles = useCallback(
    async (fileList: FileList, existingImages: BaseImageStatus[] = []) => {
      const existingKeys = new Set(
        existingImages.map((img) => `${img.localImgName}-${img.localImgType}`)
      );

      const fileArray = Array.from(fileList);

      // 处理重复检测和HEIC转换
      const { files, duplicateCount, heicFailedCount, damagedCount } =
        await fileArray.reduce(
          async (accPromise, file) => {
            const acc = await accPromise;

            // 检查重复
            if (existingKeys.has(`${file.name}-${file.type}`)) {
              return {
                files: acc.files,
                duplicateCount: acc.duplicateCount + 1,
                heicFailedCount: acc.heicFailedCount,
                damagedCount: acc.damagedCount,
              };
            }

            let processedFile = file;

            // 处理HEIC
            if (file.type === 'image/heic') {
              const { data, error } = await tryCatchPromise(
                convertHeicToAny({ blob: file, toType: 'image/png' })
              );

              if (error) {
                // 处理HEIC转换失败
                feBot.lark_report({
                  title: '[FE_ERROR] convert Heic Error',
                  type: from,
                  errorInfo: JSON.stringify(error),
                });

                return {
                  files: acc.files,
                  duplicateCount: acc.duplicateCount,
                  heicFailedCount: acc.heicFailedCount + 1,
                  damagedCount: acc.damagedCount,
                };
              }
              processedFile = data;
            }

            // 图像完整性校验
            const isValid = await validateImageIntegrity(processedFile);
            if (!isValid) {
              return {
                files: acc.files,
                duplicateCount: acc.duplicateCount,
                heicFailedCount: acc.heicFailedCount,
                damagedCount: acc.damagedCount + 1,
              };
            }

            // 图像通过所有检查，添加到文件列表
            return {
              files: [...acc.files, processedFile],
              duplicateCount: acc.duplicateCount,
              heicFailedCount: acc.heicFailedCount,
              damagedCount: acc.damagedCount,
            };
          },

          Promise.resolve({
            files: [] as File[],
            duplicateCount: 0,
            heicFailedCount: 0,
            damagedCount: 0,
          })
        );

      // 显示处理结果提示
      const messages = [];
      if (duplicateCount > 0) {
        messages.push(`${duplicateCount} duplicate images`);
      }
      if (damagedCount > 0) {
        messages.push(`${damagedCount} damaged images`);
      }
      if (heicFailedCount > 0) {
        messages.push(`${heicFailedCount} unsupported HEIC images`);
      }
      if (messages.length > 0) {
        toast.info(`Removed ${messages.join(', ')}`);
      }

      return files;
    },
    [convertHeicToAny, from, validateImageIntegrity]
  );

  /**
   * 验证图像文件并生成本地URL（合并优化版本）
   * @param file 图像文件
   * @returns 包含成功状态、URL和清理函数的对象
   */
  const validateAndCreateImageUrl = useCallback(
    async (
      file: File
    ): Promise<{
      success: boolean;
      url?: string;
      error?: string;
      cleanup?: () => void; // 新增清理函数
    }> => {
      try {
        // 1. 先进行图像完整性校验
        const isValid = await validateImageIntegrity(file);
        if (!isValid) {
          return {
            success: false,
            error: `Image file ${file.name} is corrupted or invalid`,
          };
        }

        // 2. 创建本地URL
        const url = URL.createObjectURL(file);

        // 3. 返回成功结果和清理函数
        return {
          success: true,
          url,
          cleanup: () => {
            try {
              URL.revokeObjectURL(url);
            } catch (error) {
              console.warn(`Failed to revoke URL for ${file.name}:`, error);
            }
          },
        };
      } catch (error) {
        feBot.lark_report({
          title: '[FE_ERROR] validateAndCreateImageUrl',
          type: from,
          errorInfo: JSON.stringify(error),
        });

        return {
          success: false,
          error:
            error instanceof Error
              ? error.message
              : `Failed to process ${file.name}`,
        };
      }
    },
    [from, validateImageIntegrity]
  );

  return {
    processImageFiles,
    validateAndCreateImageUrl,
  };
}

export default useImageProcessor;
